import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { storageManager } from '../utils/storage';
import { debugAuthToken } from '../utils/dataSync';

export default function DebugView() {
  // 添加一个ref，用于跟踪组件是否已经初始化过
  const isInitializedRef = useRef(false);
  // 添加测试API状态的ref
  const isTestingApiRef = useRef(false);
  // 添加D1数据库加载状态的ref
  const isLoadingD1Ref = useRef(false);

  const [debugInfo, setDebugInfo] = useState({
    context: {
      schoolYearId: null as string | null,
      termId: null as string | null,
      schoolYearName: '',
      termName: ''
    },
    stats: {
      allStudents: 0,
      filteredStudents: 0,
      allPayments: 0,
      filteredPayments: 0,
      allAttendance: 0,
      filteredAttendance: 0,
      allGrades: 0,
      filteredGrades: 0,
      allClasses: 0,
      filteredClasses: 0,
      allSubjects: 0,
      filteredSubjects: 0,
      allTeachers: 0,
      filteredTeachers: 0,
      allTeacherAttendance: 0,
      filteredTeacherAttendance: 0,
      allSchoolYears: 0,
      filteredSchoolYears: 0,
      allCustomReports: 0,
      filteredCustomReports: 0,
      allAlertRules: 0,
      filteredAlertRules: 0,
      allCustomExams: 0,
      filteredCustomExams: 0,
      allPreferences: 0,
      filteredPreferences: 0
    },
    localStorage: {} as Record<string, any>,
    d1Data: {} as Record<string, any>
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<string>('');
  const [actionResult, setActionResult] = useState<{message: string, severity: 'success' | 'error'} | null>(null);
  const [isTestingApi, setIsTestingApi] = useState(false);
  const [isLoadingD1, setIsLoadingD1] = useState(false);
  const [apiTestResult, setApiTestResult] = useState<{success: boolean, message: string, details: string} | null>(null);

  // 刷新调试信息
  const refreshDebugInfo = useCallback(() => {
    try {
      console.log('【DEBUG】刷新调试信息');
      
      // 获取当前上下文
      const context = storageManager.getCurrentContext();
      console.log('【DEBUG】当前上下文:', context);
      
      // 统计localStorage中的条目数
      const localStorageInfo: Record<string, any> = {};
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          try {
            const value = localStorage.getItem(key);
            if (value) {
              try {
                // 尝试解析为JSON
                localStorageInfo[key] = JSON.parse(value);
              } catch (e) {
                // 如果不是JSON则保存原始字符串
                localStorageInfo[key] = value;
              }
            }
          } catch (e) {
            console.error(`读取localStorage键[${key}]出错:`, e);
            localStorageInfo[key] = `[错误: ${e instanceof Error ? e.message : '未知错误'}]`;
          }
        }
      }
      
      // 统计不同集合的数据量
      const getAll = (key: string) => {
        try {
          const rawData = localStorage.getItem(key);
          if (!rawData) return [];
          return JSON.parse(rawData);
        } catch (e) {
          console.error(`解析${key}数据失败:`, e);
          return [];
        }
      };
      
      // 统计基础数据
      const allStudents = getAll('students');
      const allPayments = getAll('payments'); 
      const allAttendance = getAll('attendance');
      const allGrades = getAll('grades');
      const allClasses = getAll('classes');
      const allSubjects = getAll('subjects');
      const allTeachers = getAll('teachers');
      const allTeacherAttendance = getAll('teacher_attendance');
      const allSchoolYears = getAll('schoolYears');
      const allCustomReports = getAll('custom_reports');
      const allAlertRules = getAll('alert_rules');
      const allCustomExams = getAll('custom_exams');
      
      // 获取偏好设置（对象类型）
      const getPreferences = () => {
        try {
          const rawData = localStorage.getItem('preferences');
          if (!rawData) return {};
          return JSON.parse(rawData);
        } catch (e) {
          console.error('解析preferences数据失败:', e);
          return {};
        }
      };
      const allPreferences = getPreferences();
      
      // 根据上下文筛选 - 确保按学年学期进行正确筛选
      const filteredStudents = context.schoolYearId ? 
        allStudents.filter((s: any) => s.schoolYearId === context.schoolYearId) : 
        allStudents;
      const filteredPayments = context.schoolYearId ? 
        allPayments.filter((p: any) => p.schoolYearId === context.schoolYearId) : 
        allPayments;
      const filteredAttendance = context.schoolYearId ? 
        allAttendance.filter((a: any) => a.schoolYearId === context.schoolYearId) : 
        allAttendance;
      const filteredGrades = context.schoolYearId ? 
        allGrades.filter((g: any) => g.schoolYearId === context.schoolYearId) : 
        allGrades;
      const filteredClasses = context.schoolYearId ? 
        allClasses.filter((c: any) => c.schoolYearId === context.schoolYearId) : 
        allClasses;
      const filteredSubjects = context.schoolYearId ? 
        allSubjects.filter((s: any) => s.schoolYearId === context.schoolYearId) : 
        allSubjects;
      const filteredTeachers = context.schoolYearId ? 
        allTeachers.filter((t: any) => t.schoolYearId === context.schoolYearId) : 
        allTeachers;
      const filteredTeacherAttendance = context.schoolYearId ? 
        allTeacherAttendance.filter((ta: any) => ta.schoolYearId === context.schoolYearId) : 
        allTeacherAttendance;
      const filteredSchoolYears = allSchoolYears; // 学年信息不需要筛选
      const filteredCustomReports = context.schoolYearId ? 
        allCustomReports.filter((cr: any) => cr.schoolYearId === context.schoolYearId) : 
        allCustomReports;
      const filteredAlertRules = allAlertRules; // 提醒规则通常不按学年筛选
      const filteredCustomExams = context.schoolYearId ? 
        allCustomExams.filter((ce: any) => ce.schoolYearId === context.schoolYearId) : 
        allCustomExams;
      const filteredPreferences = allPreferences; // 偏好设置不按学年筛选
      
      // 获取学年和学期名称信息
      let schoolYearName = '';
      let termName = '';
      
      if (context.schoolYearId) {
        // 尝试从localStorage中获取学年名称
        try {
          const schoolYears = getAll('schoolYears');
          const currentYear = schoolYears.find((y: any) => y.id === context.schoolYearId);
          if (currentYear) {
            schoolYearName = currentYear.name || '';
            
            // 如果有学期ID，尝试找到学期名称
            if (context.termId && currentYear.terms) {
              const currentTerm = currentYear.terms.find((t: any) => t.id === context.termId);
              if (currentTerm) {
                termName = currentTerm.name || '';
              }
            }
          }
        } catch (e) {
          console.error('获取学年/学期名称失败:', e);
        }
      }
      
      // 更新调试信息
      setDebugInfo({
        context: {
          schoolYearId: context.schoolYearId,
          termId: context.termId,
          schoolYearName: schoolYearName,
          termName: termName
        },
        stats: {
          allStudents: allStudents.length,
          filteredStudents: filteredStudents.length,
          allPayments: allPayments.length,
          filteredPayments: filteredPayments.length,
          allAttendance: allAttendance.length,
          filteredAttendance: filteredAttendance.length,
          allGrades: allGrades.length,
          filteredGrades: filteredGrades.length,
          allClasses: allClasses.length,
          filteredClasses: filteredClasses.length,
          allSubjects: allSubjects.length,
          filteredSubjects: filteredSubjects.length,
          allTeachers: allTeachers.length,
          filteredTeachers: filteredTeachers.length,
          allTeacherAttendance: allTeacherAttendance.length,
          filteredTeacherAttendance: filteredTeacherAttendance.length,
          allSchoolYears: allSchoolYears.length,
          filteredSchoolYears: filteredSchoolYears.length,
          allCustomReports: allCustomReports.length,
          filteredCustomReports: filteredCustomReports.length,
          allAlertRules: allAlertRules.length,
          filteredAlertRules: filteredAlertRules.length,
          allCustomExams: allCustomExams.length,
          filteredCustomExams: filteredCustomExams.length,
          allPreferences: Object.keys(allPreferences).length,
          filteredPreferences: Object.keys(filteredPreferences).length
        },
        localStorage: localStorageInfo,
        d1Data: debugInfo.d1Data
      });
      
      console.log('【DEBUG】调试信息已更新');
    } catch (error) {
      console.error('刷新调试信息失败:', error);
    }
  }, [debugInfo.d1Data]);

  // 测试API连接
  const testApiConnection = useCallback(async () => {
    // 防止重复请求
    if (isTestingApiRef.current) {
      console.log('【DEBUG】已有API测试正在进行中');
      return;
    }
    
    isTestingApiRef.current = true;
    setIsTestingApi(true);
    
    try {
      console.log('【DEBUG】开始测试API连接');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        try {
          controller.abort();
        } catch (abortError) {
          // 忽略abort时的错误，这可能是浏览器扩展导致的
          console.warn('AbortController.abort() 出现错误，已忽略:', abortError);
        }
      }, 5000); // 5秒超时
      
      // 使用代理路径进行测试
      const response = await fetch('/api/debug/test', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        signal: controller.signal
      });
      
      // 安全地清除超时
      try {
        clearTimeout(timeoutId);
      } catch (clearError) {
        console.warn('clearTimeout 出现错误，已忽略:', clearError);
      }
      
      if (response.ok) {
        const data = await response.json();
        setApiTestResult({
          success: true,
          message: data.message || '连接成功',
          details: JSON.stringify(data, null, 2)
        });
        console.log('【DEBUG】API测试成功:', data);
      } else {
        const errorText = await response.text();
        setApiTestResult({
          success: false,
          message: `连接失败: ${response.status} ${response.statusText}`,
          details: errorText || '无详细错误信息'
        });
        console.error('【DEBUG】API测试失败:', response.status, response.statusText);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      
      // 过滤掉浏览器扩展相关错误
      if (errorMessage.includes('message port closed') ||
          errorMessage.includes('Extension context invalidated') ||
          errorMessage.includes('runtime.lastError')) {
        console.warn('检测到浏览器扩展相关错误，已忽略:', errorMessage);
        setApiTestResult({
          success: false,
          message: '连接测试被浏览器扩展干扰',
          details: '某些浏览器扩展可能会干扰网络请求。建议：\n1. 尝试在隐私模式下访问\n2. 禁用所有扩展后重试\n3. 使用其他浏览器测试'
        });
        return;
      }
      
      console.error('【DEBUG】API测试错误:', errorMessage);
      
      // 特殊处理超时错误
      if (errorMessage.includes('abort')) {
        setApiTestResult({
          success: false,
          message: '连接超时',
          details: '请求在5秒内未收到响应。请检查服务器是否在运行。'
        });
      } else {
        setApiTestResult({
          success: false,
          message: `测试失败: ${errorMessage}`,
          details: error instanceof Error && error.stack ? error.stack : '无错误堆栈'
        });
      }
    } finally {
      isTestingApiRef.current = false;
      setIsTestingApi(false);
    }
  }, []);



  // 使用 useCallback 包装 fetchD1Data 函数
  const fetchD1Data = useCallback(async () => {
    // 使用ref防止多次重复请求
    if (isLoadingD1Ref.current) {
      console.log('【DEBUG】已有D1数据请求正在进行中，忽略此次请求');
      return;
    }
    
    // 设置加载状态 - 同时更新ref和状态
    isLoadingD1Ref.current = true;
    setIsLoadingD1(true);
    console.log('【DEBUG】开始获取D1数据库数据');

    try {
      const tables = [
        'students', 
        'classes', 
        'attendance', 
        'teacher_attendance',
        'grades',
        'payments', 
        'subjects', 
        'schoolYears',
        'teachers',
        'custom_reports',
        'alert_rules',
        'preferences',
        'current_context'
      ];
      
      const d1Data: Record<string, any> = {};
      
      // 获取认证令牌
      const authToken = localStorage.getItem('auth_token');
      if (!authToken) {
        console.error('获取D1数据库数据失败: 未找到认证令牌');
        setActionResult({
          message: '获取D1数据库数据失败: 请先登录获取认证令牌',
          severity: 'error'
        });
        isLoadingD1Ref.current = false;
        setIsLoadingD1(false);
        return;
      }
      
      // 通过API获取D1数据
      for (const table of tables) {
        try {
          // 构建API URL - 使用动态路径（支持 Cloudflare Pages 同源部署）
          const apiUrl = `/api/d1/${table}`;
          console.log(`请求D1数据: ${apiUrl}`);
          
          // 设置一个超时，防止请求无限等待
          const controller = new AbortController();
          const timeoutId = setTimeout(() => {
            try {
              controller.abort();
            } catch (abortError) {
              // 忽略abort时的错误，这可能是浏览器扩展导致的
              console.warn(`${table} AbortController.abort() 出现错误，已忽略:`, abortError);
            }
          }, 8000); // 8秒超时
          
          // 添加认证令牌到请求头
          const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            signal: controller.signal
          });
          
          // 安全地清除超时
          try {
            clearTimeout(timeoutId);
          } catch (clearError) {
            console.warn(`${table} clearTimeout 出现错误，已忽略:`, clearError);
          }
          
          const contentType = response.headers.get('content-type');
          console.log(`${table} 响应状态: ${response.status}, 内容类型: ${contentType}`);
          
          if (response.ok) {
            if (contentType && contentType.includes('application/json')) {
              const result = await response.json();
              // 修改：检查并计算实际数据条数
              const dataCount = Array.isArray(result.data) ? result.data.length : 0;
              const statsTotal = result.stats?.total || dataCount;
              
              d1Data[table] = {
                stats: result.stats ? { ...result.stats, total: statsTotal } : { total: dataCount },
                data: result.data
              };
              console.log(`获取D1 ${table}数据成功: ${statsTotal}条记录`);
            } else {
              // 不是JSON格式的响应
              const textResponse = await response.text();
              console.error(`${table} 响应不是JSON格式:`, textResponse.substring(0, 100));
              d1Data[table] = { 
                error: `响应格式错误: 预期JSON，但收到 ${contentType}`,
                errorDetails: textResponse.substring(0, 500) + (textResponse.length > 500 ? '...(截断)' : '')
              };
            }
          } else {
            const statusText = response.statusText;
            let errorDetails = '';
            
            try {
              // 尝试获取可能的JSON错误响应
              const contentType = response.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                const errorJson = await response.json();
                errorDetails = JSON.stringify(errorJson, null, 2);
              } else {
                // 如果不是JSON，获取文本响应
                errorDetails = await response.text();
              }
            } catch (e) {
              errorDetails = `无法解析错误响应: ${e instanceof Error ? e.message : String(e)}`;
            }
            
            d1Data[table] = { 
              error: `获取失败: ${response.status} ${statusText}`,
              errorDetails: errorDetails
            };
            console.error(`获取D1 ${table}数据失败:`, response.status, statusText);
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          
          // 过滤掉浏览器扩展相关错误
          if (errorMessage.includes('message port closed') ||
              errorMessage.includes('Extension context invalidated') ||
              errorMessage.includes('runtime.lastError')) {
            console.warn(`检测到浏览器扩展相关错误 (${table})，已忽略:`, errorMessage);
            d1Data[table] = { 
              error: '请求被浏览器扩展干扰',
              errorDetails: '某些浏览器扩展可能会干扰网络请求。建议：\n1. 尝试在隐私模式下访问\n2. 禁用所有扩展后重试\n3. 使用其他浏览器测试'
            };
            continue; // 继续处理下一个表
          }
          
          console.error(`请求D1 ${table}数据出错:`, errorMessage);
          
          // 特殊处理超时错误
          if (errorMessage.includes('abort')) {
            d1Data[table] = { 
              error: '请求超时',
              errorDetails: `获取数据时请求超时。请检查服务器是否正在运行并且正确配置了API端点。\n\n错误详情: ${errorMessage}`
            };
          } else {
            d1Data[table] = { 
              error: errorMessage,
              errorDetails: `错误堆栈: ${error instanceof Error && error.stack ? error.stack : '无堆栈信息'}`
            };
          }
        }
      }
      
      console.log('【DEBUG】D1数据库数据获取完成，即将更新状态');
      
      setDebugInfo(prev => ({
        ...prev,
        d1Data: d1Data
      }));
      
    } catch (error) {
      console.error('获取D1数据库数据失败:', error);
    } finally {
      // 重置加载状态 - 同时更新ref和状态
      isLoadingD1Ref.current = false;
      setIsLoadingD1(false);
      console.log('【DEBUG】D1数据库数据加载状态重置');
    }
  }, []);

  useEffect(() => {
    // 只在组件第一次挂载时执行初始化
    if (!isInitializedRef.current) {
      console.log('【DEBUG】组件首次渲染，执行初始化');
      // 标记为已初始化
      isInitializedRef.current = true;
      
      // 执行初始数据加载（仅加载本地调试信息，不自动刷新D1数据库）
      refreshDebugInfo();
      // 移除自动刷新D1数据库的调用 - fetchD1Data();
      
      // 监听上下文变更
      const handleContextChange = () => {
        console.log("DebugView: Context changed, refreshing debug info...");
        refreshDebugInfo();
      };
      
      window.addEventListener('contextChange', handleContextChange);
      
      // 返回清理函数
      return () => {
        window.removeEventListener('contextChange', handleContextChange);
      };
    }
  }, [refreshDebugInfo]); // 移除fetchD1Data依赖

  const forceRefresh = useCallback(() => {
    refreshDebugInfo();
  }, [refreshDebugInfo]);

  const handleRunMigration = useCallback(() => {
    try {
      const result = storageManager.migrateDataToCurrentContext();
      setActionResult({
        message: result.success 
          ? `迁移成功! 共迁移 ${result.stats.total} 条数据` 
          : '迁移失败',
        severity: result.success ? 'success' : 'error'
      });
      refreshDebugInfo();
    } catch (error) {
      setActionResult({
        message: `迁移出错: ${error instanceof Error ? error.message : '未知错误'}`,
        severity: 'error'
      });
    }
  }, [refreshDebugInfo]);

  const handleClearAllData = useCallback(() => {
    setDialogAction('clearAll');
    setDialogOpen(true);
  }, []);

  const handleClearCache = useCallback(() => {
    setDialogAction('clearCache');
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleConfirmAction = useCallback(() => {
    if (dialogAction === 'clearAll') {
      try {
        // 清空所有localStorage数据
        localStorage.clear();
        setActionResult({
          message: '所有数据已成功清空',
          severity: 'success'
        });
        refreshDebugInfo();
      } catch (error) {
        setActionResult({
          message: `清空数据失败: ${error instanceof Error ? error.message : '未知错误'}`,
          severity: 'error'
        });
      }
    } else if (dialogAction === 'clearCache') {
      try {
        // 清除所有缓存但保留重要系统数据
        const keysToKeep = ['current_context', 'auth_token', 'user_info'];
        
        // 备份重要数据
        const backups: Record<string, string> = {};
        keysToKeep.forEach(key => {
          const value = localStorage.getItem(key);
          if (value) {
            backups[key] = value;
          }
        });
        
        // 清空所有数据
        localStorage.clear();
        
        // 恢复重要数据
        Object.entries(backups).forEach(([key, value]) => {
          localStorage.setItem(key, value);
        });
        
        setActionResult({
          message: '所有缓存已成功清除（保留系统信息）',
          severity: 'success'
        });
        refreshDebugInfo();
      } catch (error) {
        setActionResult({
          message: `清除缓存失败: ${error instanceof Error ? error.message : '未知错误'}`,
          severity: 'error'
        });
      }
    }
    
    setDialogOpen(false);
  }, [dialogAction, refreshDebugInfo]);

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 3 },
      maxWidth: '100%',
      overflow: 'hidden',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <Typography variant="h5" gutterBottom sx={{ 
        fontSize: { xs: '1.2rem', sm: '1.5rem' },
        wordBreak: 'break-word'
      }}>
        系统调试信息
      </Typography>
      
      {actionResult && (
        <Alert 
          severity={actionResult.severity} 
          sx={{ mb: 3 }}
          onClose={() => setActionResult(null)}
        >
          {actionResult.message}
        </Alert>
      )}
      
      {apiTestResult && (
        <Alert 
          severity={apiTestResult.success ? 'success' : 'error'} 
          sx={{ mb: 3 }}
          onClose={() => setApiTestResult(null)}
        >
          <Typography fontWeight="bold">{apiTestResult.message}</Typography>
          {apiTestResult.details && (
            <Box 
              component="pre" 
              sx={{ 
                mt: 1, 
                fontSize: '0.75rem', 
                p: 1, 
                backgroundColor: 'rgba(0,0,0,0.05)',
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: 100 
              }}
            >
              {apiTestResult.details}
            </Box>
          )}
        </Alert>
      )}
      
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button 
            variant="outlined" 
            size="small"
            onClick={forceRefresh}
          >
            刷新调试信息
          </Button>
          
          <Button 
            variant="outlined"
            size="small" 
            onClick={fetchD1Data}
            disabled={isLoadingD1}
          >
            {isLoadingD1 ? <CircularProgress size={16} /> : '刷新D1数据库'}
          </Button>
          
          <Button 
            variant="outlined"
            size="small" 
            color="info" 
            onClick={testApiConnection}
            disabled={isTestingApi}
          >
            {isTestingApi ? <CircularProgress size={16} /> : '测试API连接'}
          </Button>
          
          <Button 
            variant="outlined"
            size="small" 
            color="success" 
            onClick={handleRunMigration}
          >
            运行数据迁移
          </Button>
          
          <Button 
            variant="outlined"
            size="small" 
            color="error" 
            onClick={handleClearAllData}
          >
            清空所有数据
          </Button>
          
          <Button 
            variant="outlined"
            size="small" 
            color="error" 
            onClick={handleClearCache}
          >
            清除所有缓存
          </Button>
          
                     <Button 
             variant="outlined"
             size="small" 
             color="secondary" 
             onClick={() => {
               try {
                 debugAuthToken();
                 setActionResult({
                   message: '调试令牌信息已输出到控制台，请查看浏览器开发者工具',
                   severity: 'success'
                 });
               } catch (error) {
                 setActionResult({
                   message: `调试令牌失败: ${error instanceof Error ? error.message : '未知错误'}`,
                   severity: 'error'
                 });
               }
             }}
           >
             调试令牌
           </Button>
           
           <Button 
             variant="outlined"
             size="small" 
             color="warning" 
             onClick={() => {
               try {
                 console.log('重新载入监听 - 开始刷新本地调试数据');
                 refreshDebugInfo();
                 // 移除自动刷新D1数据库的调用 - fetchD1Data();
                 setActionResult({
                    message: '已触发重新载入监听，正在刷新本地调试数据（不包含D1数据库）',
                    severity: 'success'
                  });
               } catch (error) {
                 setActionResult({
                   message: `重新载入监听失败: ${error instanceof Error ? error.message : '未知错误'}`,
                   severity: 'error'
                 });
               }
             }}
           >
             重新载入监听
           </Button>
        </Stack>
      </Box>
      
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ 
          fontSize: { xs: '1rem', sm: '1.25rem' },
          wordBreak: 'break-word'
        }}>
          当前上下文
        </Typography>
        <TableContainer sx={{ 
          overflow: 'auto',
          maxWidth: '100%'
        }}>
          <Table size="small" sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  fontWeight: 'bold'
                }}>
                  属性
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  fontWeight: 'bold'
                }}>
                  值
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  wordBreak: 'break-word'
                }}>
                  学年ID
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  wordBreak: 'break-all'
                }}>
                  {debugInfo.context.schoolYearId || '未设置'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  wordBreak: 'break-word'
                }}>
                  学年名称
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  wordBreak: 'break-word'
                }}>
                  {debugInfo.context.schoolYearName || '未设置'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  wordBreak: 'break-word'
                }}>
                  学期ID
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  wordBreak: 'break-all'
                }}>
                  {debugInfo.context.termId || '未设置'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  wordBreak: 'break-word'
                }}>
                  学期名称
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  wordBreak: 'break-word'
                }}>
                  {debugInfo.context.termName || '未设置'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  wordBreak: 'break-word'
                }}>
                  上下文是否有效
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                }}>
                  {storageManager.hasValidContext() ? '有效' : '无效'}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ 
          fontSize: { xs: '1rem', sm: '1.25rem' },
          wordBreak: 'break-word'
        }}>
          数据筛选统计
        </Typography>
        <TableContainer sx={{ 
          overflow: 'auto',
          maxWidth: '100%',
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}>
          <Table size="small" sx={{ 
            width: '100%',
            tableLayout: 'auto'
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  wordBreak: { xs: 'break-word', sm: 'normal' },
                  fontWeight: 'bold'
                }}>
                  数据类型
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  fontWeight: 'bold'
                }}>
                  全部
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  fontWeight: 'bold'
                }}>
                  筛选后
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center',
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  wordBreak: { xs: 'break-word', sm: 'normal' },
                  fontWeight: 'bold'
                }}>
                  是否筛选
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  wordBreak: { xs: 'break-word', sm: 'normal' }
                }}>学生记录</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.allStudents}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.filteredStudents}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>
                  {debugInfo.stats.allStudents !== debugInfo.stats.filteredStudents ? '是' : '否'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  wordBreak: { xs: 'break-word', sm: 'normal' }
                }}>支付记录</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.allPayments}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.filteredPayments}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>
                  {debugInfo.stats.allPayments !== debugInfo.stats.filteredPayments ? '是' : '否'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  wordBreak: { xs: 'break-word', sm: 'normal' }
                }}>考勤记录</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.allAttendance}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.filteredAttendance}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>
                  {debugInfo.stats.allAttendance !== debugInfo.stats.filteredAttendance ? '是' : '否'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  wordBreak: { xs: 'break-word', sm: 'normal' }
                }}>成绩记录</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.allGrades}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.filteredGrades}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>
                  {debugInfo.stats.allGrades !== debugInfo.stats.filteredGrades ? '是' : '否'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  wordBreak: { xs: 'break-word', sm: 'normal' }
                }}>班级信息</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.allClasses}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.filteredClasses}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>
                  {debugInfo.stats.allClasses !== debugInfo.stats.filteredClasses ? '是' : '否'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  wordBreak: { xs: 'break-word', sm: 'normal' }
                }}>科目信息</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.allSubjects}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.filteredSubjects}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>
                  {debugInfo.stats.allSubjects !== debugInfo.stats.filteredSubjects ? '是' : '否'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  wordBreak: { xs: 'break-word', sm: 'normal' }
                }}>教师信息</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.allTeachers}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.filteredTeachers}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>
                  {debugInfo.stats.allTeachers !== debugInfo.stats.filteredTeachers ? '是' : '否'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  wordBreak: { xs: 'break-word', sm: 'normal' }
                }}>教师考勤</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.allTeacherAttendance}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.filteredTeacherAttendance}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>
                  {debugInfo.stats.allTeacherAttendance !== debugInfo.stats.filteredTeacherAttendance ? '是' : '否'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  wordBreak: { xs: 'break-word', sm: 'normal' }
                }}>学年信息</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.allSchoolYears}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.filteredSchoolYears}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>
                  {debugInfo.stats.allSchoolYears !== debugInfo.stats.filteredSchoolYears ? '是' : '否'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  wordBreak: { xs: 'break-word', sm: 'normal' }
                }}>自定义报告</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.allCustomReports}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.filteredCustomReports}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>
                  {debugInfo.stats.allCustomReports !== debugInfo.stats.filteredCustomReports ? '是' : '否'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  wordBreak: { xs: 'break-word', sm: 'normal' }
                }}>提醒规则</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.allAlertRules}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.filteredAlertRules}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>
                  {debugInfo.stats.allAlertRules !== debugInfo.stats.filteredAlertRules ? '是' : '否'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  wordBreak: { xs: 'break-word', sm: 'normal' }
                }}>自定义考试</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.allCustomExams}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.filteredCustomExams}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>
                  {debugInfo.stats.allCustomExams !== debugInfo.stats.filteredCustomExams ? '是' : '否'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  wordBreak: { xs: 'break-word', sm: 'normal' }
                }}>偏好设置</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.allPreferences}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>{debugInfo.stats.filteredPreferences}</TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textAlign: 'center'
                }}>
                  {debugInfo.stats.allPreferences !== debugInfo.stats.filteredPreferences ? '是' : '否'}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ 
            fontSize: { xs: '0.9rem', sm: '1rem' },
            wordBreak: 'break-word'
          }}>
            localStorage 数据
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {Object.entries(debugInfo.localStorage).map(([key, value]) => (
              <Accordion key={key}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ 
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    wordBreak: 'break-all',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {key}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ 
                    overflow: 'auto',
                    maxWidth: '100%',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#888',
                      borderRadius: '4px',
                    },
                  }}>
                    <Typography 
                      component="pre" 
                      sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word',
                        maxWidth: '100%',
                        margin: 0,
                        padding: 1,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1,
                        fontFamily: 'monospace'
                      }}
                    >
                      {JSON.stringify(value, null, 2)}
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
      
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ 
            fontSize: { xs: '0.9rem', sm: '1rem' },
            wordBreak: 'break-word'
          }}>
            D1 数据库数据
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {isLoadingD1 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : Object.keys(debugInfo.d1Data).length === 0 ? (
              <Box>
                <Typography>暂无D1数据库数据，请点击"刷新D1数据库"按钮获取</Typography>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    如果刷新后仍无法获取数据，可能是因为：
                  </Typography>
                  <ol>
                    <li>D1数据库服务未配置或连接配置有误</li>
                    <li>数据库中没有数据</li>
                    <li>服务器API路由未正确配置</li>
                  </ol>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    请查看服务器控制台日志以获取更多信息。
                  </Typography>
                </Alert>
              </Box>
            ) : (
              Object.entries(debugInfo.d1Data).map(([table, tableData]: [string, any]) => (
                <Accordion key={table}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ 
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      wordBreak: 'break-word',
                      overflow: 'hidden'
                    }}>
                      {table}
                      {tableData.stats && 
                        ` (${tableData.stats.total || (Array.isArray(tableData.data) ? tableData.data.length : 0)}条记录)`}
                      {(() => {
                        const context = debugInfo.context;
                        if (context?.schoolYearId && Array.isArray(tableData.data)) {
                          const filteredCount = tableData.data.filter((item: any) => 
                            item.schoolYearId === context.schoolYearId
                          ).length;
                          return (
                            <Typography component="span" color="primary" sx={{ 
                              ml: 1,
                              fontSize: 'inherit',
                              fontWeight: 'bold'
                            }}>
                              - 当前学年: {filteredCount}条
                            </Typography>
                          );
                        }
                        return null;
                      })()}
                      {tableData.error && 
                        <Typography component="span" color="error" sx={{ 
                          ml: 1,
                          fontSize: 'inherit'
                        }}>
                          - 获取失败
                        </Typography>
                      }
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {tableData.error ? (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography fontWeight="bold">{tableData.error}</Typography>
                        {tableData.errorDetails && (
                          <Box 
                            component="pre" 
                            sx={{ 
                              mt: 1, 
                              fontSize: '0.75rem', 
                              p: 1, 
                              backgroundColor: 'rgba(0,0,0,0.05)',
                              borderRadius: 1,
                              overflow: 'auto',
                              maxHeight: 200 
                            }}
                          >
                            {tableData.errorDetails}
                          </Box>
                        )}
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" fontWeight="bold">可能的解决方案:</Typography>
                          <ul>
                            <li>确保D1数据库服务已正确配置</li>
                            <li>检查服务器日志看是否有错误信息</li>
                            <li>验证API路由是否正确配置 (/api/debug/d1/{table})</li>
                            <li>尝试重启服务器</li>
                          </ul>
                        </Box>
                      </Alert>
                    ) : (
                      <>
                        {tableData.stats && (
                          <Paper sx={{ p: 2, mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>统计信息</Typography>
                            <TableContainer sx={{ 
                              overflow: 'auto',
                              maxWidth: '100%'
                            }}>
                              <Table size="small" sx={{ width: '100%' }}>
                                <TableBody>
                                  {Object.entries(tableData.stats).map(([key, value]) => (
                                    <TableRow key={key}>
                                      <TableCell component="th" scope="row" sx={{ 
                                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                        wordBreak: 'break-word'
                                      }}>
                                        {key}
                                      </TableCell>
                                      <TableCell sx={{ 
                                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                        wordBreak: 'break-all'
                                      }}>
                                        {String(value)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  {/* 添加当前学年学期筛选后的统计 */}
                                  {(() => {
                                    const context = debugInfo.context;
                                    if (context?.schoolYearId && Array.isArray(tableData.data)) {
                                      const filteredData = tableData.data.filter((item: any) => 
                                        item.schoolYearId === context.schoolYearId
                                      );
                                      return (
                                        <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                                          <TableCell component="th" scope="row" sx={{ 
                                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                            wordBreak: 'break-word',
                                            fontWeight: 'bold'
                                          }}>
                                            当前学年筛选后
                                          </TableCell>
                                          <TableCell sx={{ 
                                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                            wordBreak: 'break-all',
                                            fontWeight: 'bold'
                                          }}>
                                            {filteredData.length}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    }
                                    return null;
                                  })()}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Paper>
                        )}
                        {(() => {
                          const context = debugInfo.context;
                          const hasSchoolYearFilter = context?.schoolYearId && Array.isArray(tableData.data);
                          const filteredData = hasSchoolYearFilter ? 
                            tableData.data.filter((item: any) => item.schoolYearId === context.schoolYearId) : 
                            null;
                          
                          return (
                            <>
                              {hasSchoolYearFilter && (
                                <>
                                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                                    当前学年数据内容 ({context.schoolYearName || context.schoolYearId})
                                  </Typography>
                                  {filteredData && filteredData.length === 0 ? (
                                    <Alert severity="info" sx={{ mb: 2 }}>当前学年暂无数据</Alert>
                                  ) : (
                                    <Box sx={{ 
                                      overflow: 'auto',
                                      maxWidth: '100%',
                                      maxHeight: 200,
                                      mb: 2,
                                      '&::-webkit-scrollbar': {
                                        width: '8px',
                                        height: '8px',
                                      },
                                      '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                      },
                                      '&::-webkit-scrollbar-thumb': {
                                        background: '#888',
                                        borderRadius: '4px',
                                      },
                                    }}>
                                      <Typography 
                                        component="pre" 
                                        sx={{ 
                                          fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-all',
                                          overflowWrap: 'break-word',
                                          maxWidth: '100%',
                                          margin: 0,
                                          padding: 1,
                                          backgroundColor: '#e3f2fd',
                                          borderRadius: 1,
                                          fontFamily: 'monospace'
                                        }}
                                      >
                                        {JSON.stringify(filteredData, null, 2)}
                                      </Typography>
                                    </Box>
                                  )}
                                </>
                              )}
                              
                              <Typography variant="subtitle2" gutterBottom>
                                全部数据内容 {hasSchoolYearFilter && '(所有学年)'}
                              </Typography>
                              {Array.isArray(tableData.data) && tableData.data.length === 0 ? (
                                <Alert severity="info">表中没有数据</Alert>
                              ) : (
                                <Box sx={{ 
                                  overflow: 'auto',
                                  maxWidth: '100%',
                                  maxHeight: 300,
                                  '&::-webkit-scrollbar': {
                                    width: '8px',
                                    height: '8px',
                                  },
                                  '&::-webkit-scrollbar-track': {
                                    background: '#f1f1f1',
                                  },
                                  '&::-webkit-scrollbar-thumb': {
                                    background: '#888',
                                    borderRadius: '4px',
                                  },
                                }}>
                                  <Typography 
                                    component="pre" 
                                    sx={{ 
                                      fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-all',
                                      overflowWrap: 'break-word',
                                      maxWidth: '100%',
                                      margin: 0,
                                      padding: 1,
                                      backgroundColor: '#f5f5f5',
                                      borderRadius: 1,
                                      fontFamily: 'monospace'
                                    }}
                                  >
                                    {JSON.stringify(tableData.data, null, 2)}
                                  </Typography>
                                </Box>
                              )}
                            </>
                          );
                        })()}
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {/* 确认对话框 */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
      >
        <DialogTitle>确认操作</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialogAction === 'clearAll' && 
              '确定要清空所有数据吗？此操作不可恢复！'}
            {dialogAction === 'clearCache' && 
              '确定要清除所有缓存数据吗？此操作不可恢复！'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button onClick={handleConfirmAction} color="error" autoFocus>
            确认
          </Button>
        </DialogActions>
      </Dialog>

      {/* 高级调试工具 */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ 
          fontSize: { xs: '1rem', sm: '1.25rem' },
          color: 'primary.main',
          fontWeight: 'bold'
        }}>
          高级调试工具
        </Typography>
        
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          mt: 2
        }}>
          {/* 认证调试区域 */}
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ 
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 'bold',
              color: 'text.primary'
            }}>
              认证调试
            </Typography>
            <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} sx={{ flexWrap: 'wrap' }}>
              <Button 
                variant="outlined"
                size="small"
                onClick={() => {
                  debugAuthToken();
                  alert('调试信息已输出到控制台，请打开开发者工具查看');
                }}
                sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  px: { xs: 1, sm: 2 }
                }}
              >
                测试认证令牌
              </Button>
              <Button 
                variant="outlined"
                color="error"
                size="small"
                onClick={() => {
                  localStorage.removeItem('auth_token');
                  localStorage.removeItem('auth_user');
                  localStorage.removeItem('refresh_token');
                  alert('已清除所有认证令牌，请重新登录');
                  window.location.href = '/login';
                }}
                sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  px: { xs: 1, sm: 2 }
                }}
              >
                清除令牌并重新登录
              </Button>
            </Stack>
          </Box>

          {/* D1数据库调试区域 */}
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ 
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 'bold',
              color: 'text.primary'
            }}>
              D1数据库状态
            </Typography>
            <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} sx={{ flexWrap: 'wrap' }}>
              <Button 
                variant="outlined"
                color="success"
                size="small"
                onClick={() => {
                  const headers = {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                  };
                  
                  // 添加一个测试用户到D1数据库
                  fetch('/api/auth/add-test-user', {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                      secret: 'debug_secret_key',
                      username: 'admin',
                      password: 'admin123',
                      role: 'admin'
                    })
                  })
                    .then(res => res.json())
                    .then(data => {
                      console.log('添加测试用户结果:', data);
                      alert(`测试用户添加${data.success ? '成功' : '失败'}: ${data.message}`);
                    })
                    .catch(err => {
                      console.error('添加测试用户错误:', err);
                      alert('添加测试用户时发生错误: ' + err.message);
                    });
                }}
                sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  px: { xs: 1, sm: 2 }
                }}
              >
                添加测试用户
              </Button>
              <Button 
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => {
                  const headers = {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                  };
                  
                  // 初始化数据库表
                  fetch('/api/d1/init-tables', {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                      secret: 'debug_secret_key'
                    })
                  })
                    .then(res => res.json())
                    .then(data => {
                      console.log('初始化数据库表结果:', data);
                      alert(`数据库表初始化${data.success ? '成功' : '失败'}: ${data.message}`);
                    })
                    .catch(err => {
                      console.error('初始化数据库表错误:', err);
                      alert('初始化数据库表时发生错误: ' + err.message);
                    });
                }}
                sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  px: { xs: 1, sm: 2 }
                }}
              >
                初始化数据库表
              </Button>
              <Button 
                variant="outlined"
                color="info"
                size="small"
                onClick={() => {
                  // 刷新页面，重新加载数据
                  window.location.reload();
                }}
                sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  px: { xs: 1, sm: 2 }
                }}
              >
                刷新页面
              </Button>
            </Stack>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
} 